import { create } from 'zustand';
import { createClient } from '@supabase/supabase-js';
import logger from '@/logger';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
const supabase = createClient(supabaseUrl, supabaseKey);

export interface OrganizationAgent {
	id: string;
	organization_id: string;
	agent_type: string;
	is_active: boolean;
	system_prompt: string | null;
	model: string;
	temperature: number;
	max_tokens: number;
	transition_message: string | null;
	transition_message_where_it_happens: string | null;
	social_network_analysis_failure_message: string | null;
	metadata: Record<string, any> | null;
	created_at: string;
	updated_at: string;
}

export interface CreateAgentData {
	organization_id: string;
	agent_type: string;
	is_active?: boolean;
	system_prompt?: string;
	model?: string;
	temperature?: number;
	max_tokens?: number;
	metadata?: Record<string, any>;
}

interface AgentStore {
	agents: OrganizationAgent[];
	selectedAgent: OrganizationAgent | null;
	isLoading: boolean;
	error: string | null;
	fetchKnowledgeBase: (agentId: string) => Promise<any | null>;
	saveKnowledgeBase: (agentId: string, markdown: string, namespace: string) => Promise<boolean>;
	loadAgents: (organizationId?: string) => Promise<void>;
	updateAgent: (agentId: string, updates: Partial<OrganizationAgent>) => Promise<boolean>;
	toggleAgentActive: (agentId: string, isActive: boolean) => Promise<boolean>;
	setSelectedAgent: (agent: OrganizationAgent | null) => void;
	resetError: () => void;
}

export const useAgentStore = create<AgentStore>((set, get) => ({
	agents: [],
	selectedAgent: null,
	isLoading: false,
	error: null,

	loadAgents: async (organizationId?: string) => {
		try {
			set({ isLoading: true, error: null });

			let query = supabase
				.from('organization_agents')
				.select('*')
				.order('created_at', { ascending: false });

			if (organizationId) {
				query = query.eq('organization_id', organizationId);
			}

			const { data, error } = await query;

			if (error) {
				logger.error('Error loading agents:', error);
				set({ error: error.message, isLoading: false });
				return;
			}

			set({
				agents: data || [],
				isLoading: false
			});
		} catch (err) {
			logger.error('Error loading agents:', err);
			set({
				error: 'Failed to load agents',
				isLoading: false
			});
		}
	},

	updateAgent: async (agentId: string, updates: Partial<OrganizationAgent>) => {
		try {
			set({ error: null });

			const instructionUpdates = {
				agent_id: agentId,
				...(updates.system_prompt !== undefined && { system_prompt: updates.system_prompt }),
				...(updates.model !== undefined && { model: updates.model }),
				...(updates.temperature !== undefined && { temperature: updates.temperature }),
				...(updates.max_tokens !== undefined && { max_tokens: updates.max_tokens }),
				...(updates.transition_message !== undefined && {
					transition_message: updates.transition_message
				}),
				...(updates.social_network_analysis_failure_message !== undefined && {
					social_network_analysis_failure_message: updates.social_network_analysis_failure_message
				})
			};

			const { data, error } = await supabase.functions.invoke('update-agent-instructions', {
				body: instructionUpdates
			});

			if (error) {
				logger.error('Error updating agent:', error);
				set({ error: error.message });
				return false;
			}

			if (data?.success === false) {
				logger.error('Error updating agent:', data.error);
				set({ error: data.error });
				return false;
			}

			const currentAgent = get().agents.find((a) => a.id === agentId);
			if (currentAgent) {
				await get().loadAgents(currentAgent.organization_id);
			}

			if (get().selectedAgent?.id === agentId) {
				set((state) => ({
					selectedAgent: state.selectedAgent ? { ...state.selectedAgent, ...updates } : null
				}));
			}

			return true;
		} catch (err) {
			logger.error('Error updating agent:', err);
			set({ error: 'Failed to update agent' });
			return false;
		}
	},

	deleteAgent: async (agentId: string) => {
		try {
			set({ error: null });

			const { error: deleteError } = await supabase
				.from('organization_agents')
				.delete()
				.eq('id', agentId);

			if (deleteError) {
				logger.error('Error deleting agent:', deleteError);
				set({ error: deleteError.message });
				return false;
			}

			set((state) => ({
				agents: state.agents.filter((a) => a.id !== agentId),
				selectedAgent: state.selectedAgent?.id === agentId ? null : state.selectedAgent
			}));

			return true;
		} catch (err) {
			logger.error('Error deleting agent:', err);
			set({ error: 'Failed to delete agent' });
			return false;
		}
	},

	toggleAgentActive: async (agentId: string, isActive: boolean) => {
		try {
			set({ error: null });

			const { error: updateError } = await supabase
				.from('organization_agents')
				.update({
					is_active: isActive,
					updated_at: new Date().toISOString()
				})
				.eq('id', agentId);

			if (updateError) {
				logger.error('Error toggling agent status:', updateError);
				set({ error: updateError.message });
				return false;
			}

			set((state) => ({
				agents: state.agents.map((a) => (a.id === agentId ? { ...a, is_active: isActive } : a)),
				selectedAgent:
					state.selectedAgent?.id === agentId
						? { ...state.selectedAgent, is_active: isActive }
						: state.selectedAgent
			}));

			return true;
		} catch (err) {
			logger.error('Error toggling agent status:', err);
			set({ error: 'Failed to toggle agent status' });
			return false;
		}
	},

	fetchKnowledgeBase: async (agentId: string) => {
		try {
			set({ error: null });

			const { data, error: fetchError } = await supabase
				.from('knowledge_base')
				.select('markdown_content_raw, created_at')
				.eq('agent_id', agentId)
				.order('created_at', { ascending: false })
				.limit(1)
				.single();

			if (fetchError) {
				if (fetchError.code === 'PGRST116') {
					return null;
				}
				logger.error('Error fetching knowledge base:', fetchError);
				set({ error: fetchError.message });
				return null;
			}

			return data;
		} catch (err) {
			logger.error('Error fetching knowledge base:', err);
			set({ error: 'Failed to fetch knowledge base' });
			return null;
		}
	},

	saveKnowledgeBase: async (agentId: string, markdown: string, namespace: string) => {
		try {
			set({ error: null });

			const { error: insertError } = await supabase.from('knowledge_base').insert({
				agent_id: agentId,
				namespace: namespace,
				markdown_content_raw: markdown
			});

			if (insertError) {
				logger.error('Error saving knowledge base:', insertError);
				set({ error: insertError.message });
				return false;
			}

			return true;
		} catch (err) {
			logger.error('Error saving knowledge base:', err);
			set({ error: 'Failed to save knowledge base' });
			return false;
		}
	},

	setSelectedAgent: (agent) => {
		set({ selectedAgent: agent });
	},

	resetError: () => set({ error: null })
}));

export const initializeAgentStore = async (organizationId?: string) => {
	const store = useAgentStore.getState();
	await store.loadAgents(organizationId);
};
